/* Celto · Markørbygger. Logikken til blocks/celto-marker-builder.liquid.
 *
 * Markørerne er renderet på serveren. Her holdes styr på hvad der er valgt,
 * tællerne i skinnen, søgningen, hvilket område man er i, bakken i bunden
 * og til sidst "læg i kurven".
 *
 * Kurven: samme vej som Fabrics egen produktformular og celto-quiz.js. Der sendes
 * en CartLinesUpdateEvent med action 'add', så kurv-skuffen opdaterer og åbner,
 * og /cart/add.js kaldes med kurvens sektioner, så skuffen kan morphe direkte.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Øre til "1.750 kr" eller "1.328,80 kr", som money_without_trailing_zeros
function money(cents) {
  const kr = Math.floor(cents / 100);
  const ore = Math.round(cents % 100);
  return kr.toLocaleString('da-DK') + (ore ? ',' + String(ore).padStart(2, '0') : '') + ' kr';
}

// "Levér hver 3. måned, 12 % i rabat" til "3 md.", samme regler som købsvalg-blokkene
function shortPlan(name) {
  const year = name.match(/hvert?\s+(\d+)/i) || name.match(/every\s+(\d+)\s+year/i);
  if (year && /[åa]r|year/i.test(name)) return year[1] + ' år';
  if (/hvert?\s+[åa]r/i.test(name) || /every\s+year/i.test(name)) return 'Hvert år';
  const month = name.match(/hver\s+(\d+)/i) || name.match(/every\s+(\d+)\s+month/i);
  if (month) return month[1] + ' md.';
  if (/hver\s+m[åa]ned/i.test(name) || /every\s+month/i.test(name)) return '1 md.';
  return name;
}

const count = (n) => (n === 1 ? '1 markør' : n + ' markører');

class CeltoMarkerBuilder extends HTMLElement {
  connectedCallback() {
    if (!this._ready) this.#init();
    this.#attach();
  }

  disconnectedCallback() {
    this.#detach();
  }

  #init() {
    this._ready = true;
    this.cfg = JSON.parse(this.querySelector('[data-cmb-config]').textContent);
    this.sel = [];
    this.mode = 'once';
    this.plan = null;
    this.feeState = this.cfg.feeState;
    this.busy = false;

    this.cards = Array.from(this.querySelectorAll('.cmb-card'));
    this.byId = new Map(this.cards.map((c) => [c.dataset.id, c]));
    this.areas = Array.from(this.querySelectorAll('.cmb-area'));
    this.searches = Array.from(this.querySelectorAll('[data-cmb-search]'));

    this.trayWrap = this.querySelector('[data-cmb-traywrap]');
    this.tray = this.trayWrap.querySelector('[data-cmb-tray]');
    this.scrim = this.querySelector('[data-cmb-scrim]');
    this.abar = this.querySelector('[data-cmb-abar]');

    this.cta = this.tray.querySelector('[data-cmb-cta]');
    this.trayBody = this.tray.querySelector('.cmb-tray__body');
    this.closeBtn = this.tray.querySelector('.cmb-tray__close');
    this.itemTpl = this.tray.querySelector('[data-cmb-item]');

    this.#bindCards();
    this.#bindSearch();
    this.#bindNavigation();
    this.#bindTray();
    this.#renderPlans();
    this.#watchCart();
    this.update();
  }

  // Bakke, scrim og områdebjælke ud i <body>, så sektionens stakkontekst ikke
  // lægger dem under headeren. Når theme editoren fjerner eller flytter blokken,
  // tages de med tilbage ind i den, og alt på document kobles fra.
  #attach() {
    document.body.append(this.scrim, this.trayWrap, this.abar);
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
    this.areas.forEach((a) => this.areaObserver.observe(a));
    this.topObserver.observe(this.querySelector('[data-cmb-top]'));
    this.barObserver.observe(this.tray.querySelector('.cmb-tray__bar'));
  }

  #detach() {
    this.#setOpen(false, { focus: false });
    this.append(this.scrim, this.trayWrap, this.abar);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
    this.areaObserver.disconnect();
    this.topObserver.disconnect();
    this.barObserver.disconnect();
    this.scroller.style.removeProperty('padding-bottom');
  }

  /* ---------- Valg ---------- */

  toggle(id) {
    const i = this.sel.indexOf(id);
    if (i === -1) this.sel.push(id);
    else this.sel.splice(i, 1);
    this.#clearError();
    this.update();
  }

  #bindCards() {
    // Knappen er strakt over hele kortet, så et tryk hvor som helst rammer den
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.cmb-toggle');
      if (btn) this.toggle(btn.closest('.cmb-card').dataset.id);
    });
  }

  totals() {
    const pct = this.mode === 'sub' && this.plan ? this.plan.pct : 0;
    const off = (cents) => Math.round((cents * pct) / 100);
    let markers = 0;
    let disc = 0;
    this.sel.forEach((id) => {
      const price = Number(this.byId.get(id).dataset.price);
      markers += price;
      disc += off(price);
    });
    const fee = this.feeState === 'add' && this.cfg.fee.variant ? this.cfg.fee.price : 0;
    disc += off(fee);
    return { n: this.sel.length, markers, fee, pct, disc, total: markers + fee - disc };
  }

  ctaState() {
    if (!this.sel.length) return { label: 'Vælg mindst én markør', ok: false };
    if (this.mode === 'sub' && !this.plan) return { label: 'Vælg hvor ofte', ok: false };
    return { label: 'Læg blodprøven i kurven', ok: true };
  }

  update() {
    const t = this.totals();

    this.cards.forEach((card) => {
      const on = this.sel.includes(card.dataset.id);
      card.classList.toggle('is-on', on);
      card.querySelector('.cmb-toggle').setAttribute('aria-pressed', String(on));
    });

    // Tællere pr. område: badge i overskrift og chips, "3 valgt" i skinnen
    const perArea = {};
    this.sel.forEach((id) => {
      const a = this.byId.get(id).dataset.area;
      perArea[a] = (perArea[a] || 0) + 1;
    });
    document.querySelectorAll('.cmb-area, .cmb-chip').forEach((el) => {
      if (!this.contains(el)) return;
      const n = perArea[el.dataset.area] || 0;
      const badge = el.querySelector('[data-area-badge]');
      badge.textContent = n;
      badge.hidden = !n;
    });
    [this, this.abar].forEach((scope) => {
      scope.querySelectorAll('[data-area-count]').forEach((el) => {
        const n = perArea[el.closest('[data-area]').dataset.area] || 0;
        el.textContent = n ? n + ' valgt' : el.dataset.areaCount;
        el.classList.toggle('is-sel', !!n);
      });
    });

    // Oversigten i bakken
    const list = this.tray.querySelector('[data-cmb-list]');
    list.replaceChildren(
      ...this.sel.map((id) => {
        const card = this.byId.get(id);
        const li = this.itemTpl.content.firstElementChild.cloneNode(true);
        li.querySelector('[data-name]').textContent = card.dataset.name;
        li.querySelector('[data-area]').textContent = card.dataset.area;
        li.querySelector('[data-price]').textContent = money(Number(card.dataset.price));
        const rm = li.querySelector('[data-rm]');
        rm.dataset.rm = id;
        rm.setAttribute('aria-label', 'Fjern ' + card.dataset.name);
        return li;
      })
    );
    this.tray.querySelector('[data-cmb-empty]').hidden = t.n > 0;

    this.tray.querySelector('[data-cmb-fee-price]').textContent =
      this.feeState === 'add' ? money(this.cfg.fee.price) : this.feeState === 'package' ? 'Dækket' : 'I kurven';
    this.tray.querySelectorAll('[data-fee-note]').forEach((p) => {
      p.hidden = p.dataset.feeNote !== this.feeState;
    });

    this.tray.querySelector('[data-cmb-sub]').hidden = this.mode !== 'sub';
    this.tray.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === this.mode)));
    this.tray.querySelectorAll('[data-plan]').forEach((b) =>
      b.setAttribute('aria-pressed', String(!!this.plan && String(this.plan.id) === b.dataset.plan))
    );

    const line = (label, value, cls) => {
      const div = document.createElement('div');
      div.className = 'cmb-line ' + cls;
      const a = document.createElement('span');
      const b = document.createElement('span');
      a.textContent = label;
      b.textContent = value;
      div.append(a, b);
      return div;
    };
    const rows = [line('Markører', money(t.markers), 'cmb-line--muted')];
    if (t.fee) rows.push(line('Grundpris', money(t.fee), 'cmb-line--muted'));
    if (t.pct) rows.push(line('Fast interval, ' + t.pct + ' %', '-' + money(t.disc), 'cmb-line--muted'));
    rows.push(line(t.pct ? 'I alt pr. gang' : 'I alt', money(t.total), 'cmb-line--grand'));
    this.tray.querySelector('[data-cmb-totals]').replaceChildren(...rows);

    this.tray.querySelector('[data-cmb-meta]').textContent = count(t.n) + ' · ' + money(t.total) + (t.pct ? ' pr. gang' : '');
    this.#syncCta();
  }

  /* ---------- Søgning ---------- */

  #bindSearch() {
    const nohits = this.querySelector('[data-cmb-nohits]');
    this.searches.forEach((input) => {
      input.addEventListener('input', () => {
        const q = input.value.trim().toLowerCase();
        // De to felter (skinnen og mobil) holdes ens
        this.searches.forEach((other) => {
          if (other !== input) other.value = input.value;
        });
        let any = false;
        this.areas.forEach((area) => {
          let shown = 0;
          area.querySelectorAll('.cmb-card').forEach((card) => {
            card.hidden = !!q && !card.dataset.find.includes(q);
            if (!card.hidden) shown++;
          });
          area.hidden = !shown;
          any = any || shown > 0;
        });
        nohits.hidden = any;
        nohits.textContent = 'Ingen markører matcher "' + input.value.trim() + '". Prøv forkortelsen, fx TSH eller ALAT.';
      });
    });
  }

  /* ---------- Områder: skinne, chips og bjælken på mobil ---------- */

  #bindNavigation() {
    const go = (e) => {
      const btn = e.target.closest('[data-go]');
      if (!btn) return;
      const target = document.getElementById(btn.dataset.go);
      if (target) target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth' });
      this.#foldAbar(false);
    };
    this.addEventListener('click', go);
    this.abar.addEventListener('click', go);

    // Hvilket område man er i: det der krydser en linje lidt under toppen
    const setCurrent = (name) => {
      [this, this.abar].forEach((scope) =>
        scope.querySelectorAll('[data-go]').forEach((b) => b.setAttribute('aria-current', String(b.dataset.area === name)))
      );
      this.abar.querySelector('[data-cmb-current]').textContent = name;
    };
    this.areaObserver = new IntersectionObserver(
      (entries) => entries.forEach((en) => en.isIntersecting && setCurrent(en.target.dataset.area)),
      { rootMargin: '-25% 0px -70% 0px' }
    );
    if (this.areas[0]) setCurrent(this.areas[0].dataset.area);

    // Bjælken på mobil: frem når områdeknapperne er rullet op under headeren
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    this.topObserver = new IntersectionObserver(
      ([en]) => {
        const gone = !en.isIntersecting && en.boundingClientRect.top < header;
        this.abar.classList.toggle('is-shown', gone);
        if (!gone) this.#foldAbar(false);
      },
      { rootMargin: '-' + Math.round(header) + 'px 0px 0px 0px' }
    );

    this.abar.querySelector('.cmb-abar__btn').addEventListener('click', () => {
      this.#foldAbar(this.abar.querySelector('.cmb-abar__list').hidden);
    });
  }

  #foldAbar(open) {
    this.abar.querySelector('.cmb-abar__btn').setAttribute('aria-expanded', String(open));
    this.abar.querySelector('.cmb-abar__list').hidden = !open;
  }

  /* ---------- Bakken ---------- */

  #renderPlans() {
    const plans = this.cfg.plans || [];
    const seg = this.tray.querySelector('[data-cmb-seg]');
    seg.hidden = !plans.length;
    this.tray.querySelector('[data-cmb-plans]').replaceChildren(
      ...plans.map((p) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cmb-plan';
        b.dataset.plan = String(p.id);
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-label', p.name);
        b.textContent = shortPlan(p.name);
        const small = document.createElement('small');
        small.textContent = '-' + p.pct + ' %';
        b.append(small);
        return b;
      })
    );
  }

  #isOpen() {
    return this.tray.dataset.open === 'true';
  }

  #setOpen(open, { focus = true } = {}) {
    this.tray.dataset.open = String(open);
    this.scrim.classList.toggle('is-open', open);
    document.documentElement.classList.toggle('cmb-tray-open', open);
    this.trayBody.inert = !open;
    this.cta.setAttribute('aria-expanded', String(open));
    this.#syncCta();
    if (focus) (open ? this.closeBtn : this.cta).focus({ preventScroll: true });
  }

  #syncCta() {
    if (this.busy) return;
    if (!this.#isOpen()) {
      this.cta.textContent = 'Se og bestil';
      this.cta.disabled = false;
      return;
    }
    const c = this.ctaState();
    this.cta.textContent = c.label;
    this.cta.disabled = !c.ok;
  }

  #bindTray() {
    // Klippet skal kende bundlinjens højde, også når teksten ombrydes på mobil
    const bar = this.tray.querySelector('.cmb-tray__bar');
    // Luft i bunden af siden, så det sidste indhold kan scrolles op over bakken.
    // På desktop er det .page-wrapper der scroller i Fabric, ikke vinduet.
    this.scroller = document.querySelector('.page-wrapper') || document.body;
    this.barObserver = new ResizeObserver(() => {
      this.tray.style.setProperty('--cmb-bar-h', bar.offsetHeight + 'px');
      this.scroller.style.paddingBottom = bar.offsetHeight + 28 + 'px';
    });

    this.cta.addEventListener('click', () => {
      if (!this.#isOpen()) return this.#setOpen(true);
      if (this.ctaState().ok) this.addToCart();
    });
    this.closeBtn.addEventListener('click', () => this.#setOpen(false));
    this.scrim.addEventListener('click', () => this.#setOpen(false));
    this.onKey = (e) => {
      if (e.key === 'Escape' && this.#isOpen()) this.#setOpen(false);
    };

    this.tray.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-rm]');
      if (rm) return this.toggle(rm.dataset.rm);
      const mode = e.target.closest('[data-mode]');
      if (mode) {
        this.mode = mode.dataset.mode;
        if (this.mode === 'once') this.plan = null;
        return this.update();
      }
      const plan = e.target.closest('[data-plan]');
      if (plan) {
        this.plan = this.cfg.plans.find((p) => String(p.id) === plan.dataset.plan) || null;
        this.update();
      }
    });
  }

  /* ---------- Kurven ---------- */

  #feeStateFrom(cart) {
    const pkgs = String(this.cfg.packageIds || '').split(',').filter(Boolean);
    const items = cart.items || [];
    if (items.some((i) => pkgs.includes(String(i.product_id)))) return 'package';
    if (items.some((i) => String(i.variant_id) === String(this.cfg.fee.variant))) return 'incart';
    return 'add';
  }

  async #fetchCart() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  #watchCart() {
    // Kommer der en pakke i kurven, eller fjernes den, skifter grundprisen med
    this.onCartUpdate = (e) => {
      Promise.resolve(e.promise)
        .catch(() => {})
        .then(() => this.#fetchCart())
        .then((cart) => {
          this.feeState = this.#feeStateFrom(cart);
          this.update();
        })
        .catch(() => {});
    };
  }

  #clearError() {
    const err = this.tray.querySelector('[data-cmb-error]');
    err.hidden = true;
    err.textContent = '';
  }

  async addToCart() {
    if (this.busy) return;
    this.busy = true;
    this.cta.disabled = true;
    this.cta.setAttribute('aria-busy', 'true');
    this.cta.textContent = 'Lægger i kurven';
    this.#clearError();

    // Tjek kurven igen lige før: dækker en pakke eller gebyret allerede grundprisen?
    try {
      this.feeState = this.#feeStateFrom(await this.#fetchCart());
    } catch (e) {
      /* Kan kurven ikke læses, bruges den kendte tilstand */
    }

    const planId = this.mode === 'sub' && this.plan ? String(this.plan.id) : null;
    const line = (id, plans) => {
      const item = { id: Number(id), quantity: 1 };
      // Kun planen på varer der faktisk har den, ellers afviser Shopify hele tilføjelsen
      if (planId && String(plans || '').split(',').includes(planId)) item.selling_plan = Number(planId);
      return item;
    };
    const items = this.sel.map((id) => line(id, this.byId.get(id).dataset.plans));
    if (this.feeState === 'add' && this.cfg.fee.variant) items.unshift(line(this.cfg.fee.variant, this.cfg.fee.plans));

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw data;

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'celto-marker-builder',
          sourceId: this.dataset.blockId,
          sections: data.sections,
          didError: false,
        },
      });

      // Lagt i kurven: start forfra, og lad kurv-skuffen tage over
      this.sel = [];
      this.mode = 'once';
      this.plan = null;
      this.busy = false;
      this.cta.removeAttribute('aria-busy');
      // Uden fokus: kurv-skuffen der åbner nu, skal have det
      this.#setOpen(false, { focus: false });
      this.update();
    } catch (err) {
      this.#fetchCart()
        .then((cart) =>
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'celto-marker-builder', sourceId: this.dataset.blockId, didError: true },
          })
        )
        .catch(() => deferred.reject(err));

      const msg = this.tray.querySelector('[data-cmb-error]');
      msg.textContent =
        (err && (err.description || err.message)) || 'Vi kunne ikke lægge blodprøven i kurven. Prøv igen om lidt.';
      msg.hidden = false;
      this.busy = false;
      this.cta.removeAttribute('aria-busy');
      this.#syncCta();
    }
  }
}

if (!customElements.get('celto-marker-builder')) customElements.define('celto-marker-builder', CeltoMarkerBuilder);
